import { Avatar, Box, Grid, type SxProps, type Theme } from '@mui/material';
import { useState } from 'react';
import { avatarBase64Converter } from '../../components/utils/imgBase64Converter';
import {
	BET_STATUS_DELETED,
	BET_STATUS_EMPTY,
	BET_STATUS_OPENED,
	COMPLETED_BET_STATUSES,
} from '../../constants';
import SeasonSummary from '../admin/seasons/types/SeasonSummary';
import CompleteBetCard from '../bets/CompleteBetCard';
import EmptyBetCard from '../bets/EmptyBetCard';
import OpenedBetCard from '../bets/OpenedBetCard';
import OpenedBetHiddenCard from '../bets/OpenedBetHiddenCard';
import { isBetDetailsHidden } from '../bets/knockoutBetPrivacy';
import Bet from '../bets/types/Bet';
import {
	gameweekBalanceChangeSx,
	gameweekExpandedCardWrapSx,
	gameweekExpandedOverlaySx,
	gameweekPlayerHeaderSx,
	gameweekPlayerRowSx,
} from './gameweekPageStyles';
import { attachSeasonPlayersToBets, sortGameweekBetsByLeagueCode } from './gameweekCalendarUtils';
import GameweekCompletedCard from './GameweekCompletedCard';
import GameweekEmptyCard from './GameweekEmptyCard';
import GameweekHiddenBetCard from './GameweekHiddenBetCard';
import GameweekNoCard from './GameweekNoCard';
import GameweekOpenedCard from './GameweekOpenedCard';

const GameweekPlayersContainer = ({
	season,
	bets,
	gameweekCardsCount,
}: {
	season: SeasonSummary;
	bets: Bet[];
	gameweekCardsCount: number;
}): JSX.Element => {
	const [expandedBetId, setExpandedBetId] = useState<string | null>(null);

	const visibleBets = attachSeasonPlayersToBets(
		bets.filter((bet) => bet.betStatus !== BET_STATUS_DELETED && bet.player?.id != null),
		season.players
	);

	const betsByPlayers: { [key: string]: Bet[] } = {};

	season.players.forEach((player) => {
		betsByPlayers[player.id] = [];
	});

	visibleBets.forEach((bet) => {
		const playerId = bet.player?.id;
		if (playerId && betsByPlayers[playerId]) {
			betsByPlayers[playerId].push(bet);
		}
	});

	Object.keys(betsByPlayers).forEach((playerId) => {
		betsByPlayers[playerId] = sortGameweekBetsByLeagueCode(betsByPlayers[playerId]);
	});

	const calculateTotalBalanceChange = (playerId: string): number => {
		const playerBets = betsByPlayers[playerId] || [];
		return playerBets.reduce((total, bet) => total + (bet.balanceChange || 0), 0);
	};

	const countEmptyBets = (playerId: string): number => {
		const totalBets = betsByPlayers[playerId] || [];
		return totalBets.filter((bet) => bet.betStatus === BET_STATUS_EMPTY).length;
	};

	const sortedPlayers = [...season.players].sort((a, b) => {
		const balanceDifference =
			calculateTotalBalanceChange(b.id) - calculateTotalBalanceChange(a.id);
		if (balanceDifference !== 0) {
			return balanceDifference;
		}

		const aBetCount = (betsByPlayers[a.id] || []).length;
		const bBetCount = (betsByPlayers[b.id] || []).length;

		if (aBetCount !== bBetCount) {
			return bBetCount - aBetCount;
		}

		const aEmptyBetCount = countEmptyBets(a.id);
		const bEmptyBetCount = countEmptyBets(b.id);

		return aEmptyBetCount - bEmptyBetCount;
	});

	const handleCardClick = (betId: string): void => {
		setExpandedBetId((prevExpandedBetId) => (prevExpandedBetId === betId ? null : betId));
	};

	const handleCloseExpandedCard = (): void => {
		setExpandedBetId(null);
	};

	return (
		<Box sx={{ m: '0 auto', my: 2, maxWidth: '25rem' }}>
			{sortedPlayers.map((player) => {
				const totalBalance = calculateTotalBalanceChange(player.id);
				return (
					<Box
						key={player.id}
						sx={
							[gameweekPlayerRowSx, { filter: expandedBetId ? 'blur(0.75px)' : 'none' }] as SxProps<Theme>
						}
					>
						<Box sx={gameweekPlayerHeaderSx}>
							<Box
								sx={
									[gameweekBalanceChangeSx(totalBalance), { mr: 1, fontSize: '1.25rem' }] as SxProps<Theme>
								}
							>
								{totalBalance.toFixed(2)}€
							</Box>
							<Avatar
								sx={{ mr: 0.5, width: 30, height: 30, border: 1, borderColor: 'gray' }}
								alt="user_avatar"
								src={avatarBase64Converter(player.avatar)}
							/>
							{player.username}
						</Box>
						<Grid container spacing={1}>
							{Array.from({ length: gameweekCardsCount }).map((_, index) => {
								const bet = betsByPlayers[player.id][index];
								return (
									<Grid item xs={6} sm={6} key={bet ? bet.id : `empty-${player.id}-${index}`}>
										{bet && bet.betStatus ? (
											<>
												{COMPLETED_BET_STATUSES.includes(bet.betStatus) && (
													<GameweekCompletedCard bet={bet} onClick={() => handleCardClick(bet.id)} />
												)}
												{BET_STATUS_OPENED === bet.betStatus &&
													(isBetDetailsHidden(bet) ? (
														<GameweekHiddenBetCard
															bet={bet}
															onClick={() => handleCardClick(bet.id)}
														/>
													) : (
														<GameweekOpenedCard bet={bet} onClick={() => handleCardClick(bet.id)} />
													))}
												{BET_STATUS_EMPTY === bet.betStatus && (
													<GameweekEmptyCard bet={bet} onClick={() => handleCardClick(bet.id)} />
												)}
											</>
										) : (
											<GameweekNoCard />
										)}
									</Grid>
								);
							})}
						</Grid>
					</Box>
				);
			})}
			{expandedBetId && (
				<Box sx={gameweekExpandedOverlaySx} onClick={handleCloseExpandedCard}>
					{visibleBets.map((bet) =>
						bet.id === expandedBetId ? (
							<Box
								key={bet.id}
								sx={gameweekExpandedCardWrapSx}
								onClick={(e) => {
									e.stopPropagation();
									handleCloseExpandedCard();
								}}
							>
								{COMPLETED_BET_STATUSES.includes(bet.betStatus) && <CompleteBetCard bet={bet} />}
								{BET_STATUS_OPENED === bet.betStatus &&
									(isBetDetailsHidden(bet) ? (
										<OpenedBetHiddenCard bet={bet} />
									) : (
										<OpenedBetCard bet={bet} />
									))}
								{BET_STATUS_EMPTY === bet.betStatus && <EmptyBetCard bet={bet} />}
							</Box>
						) : null
					)}
				</Box>
			)}
		</Box>
	);
};

export default GameweekPlayersContainer;
